import { FC, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import { MembershipValidityPeriod } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { useRateContext } from "../../Context/RateContext";
import { useRateFieldName } from "../../hooks/useRateFieldName";
import { ConfirmRateChangeDialog } from "../ConfirmRateChangeDialog/ConfirmRateChangeDialog";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardRadioGroup } from "@/dashboard-design-system/RadioGroup";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

export const ValidityRenewalModeField: FC = () => {
  const { t } = useTranslate();
  const { hasExistingPayments, canModifyValidityAtDate, unlockValidityAtDate } = useRateContext();
  const { control, setValue } = useFormContext<StepTicketsFormData>();

  const validityField = useRateFieldName("membershipValidityPeriod");
  const atDateField = useRateFieldName("membershipValidityPeriodAtDate");
  const autoRenewalField = useRateFieldName("allowAutomaticRenewal");

  const currentValidity = useWatch({ control, name: validityField });
  const allowAutomaticRenewal = useWatch({ control, name: autoRenewalField });

  const isVisible = [MembershipValidityPeriod.OneYearAfterPayment, MembershipValidityPeriod.AtDate].includes(
    currentValidity as MembershipValidityPeriod
  );

  // Pure UI state: tracks whether the user has confirmed the date-change modal
  // this session. Shared via RateContext so it also unlocks the day/month pickers.
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const values = useMemo(
    () => ({
      [MembershipValidityPeriod.OneYearAfterPayment]: allowAutomaticRenewal
        ? t("dashboard", "ticketing.renewalDate.oneYearAfterPayment")
        : t("dashboard", "ticketing.expirationDate.oneYearAfterPayment"),
      [MembershipValidityPeriod.AtDate]: t("dashboard", "ticketing.renewalDate.atDate"),
    }),
    [t, allowAutomaticRenewal]
  );

  if (!isVisible) {
    return null;
  }

  const handleChange = (value: string) => {
    const next = value as MembershipValidityPeriod;

    if (next === MembershipValidityPeriod.AtDate && hasExistingPayments && !canModifyValidityAtDate) {
      setIsConfirmOpen(true);
      return;
    }

    setValue(validityField, next, { shouldDirty: true });
    if (next !== MembershipValidityPeriod.AtDate) {
      setValue(atDateField, undefined, { shouldDirty: true });
    }
  };

  const handleConfirmAtDate = () => {
    unlockValidityAtDate();
    setValue(validityField, MembershipValidityPeriod.AtDate, { shouldDirty: true });
    setIsConfirmOpen(false);
  };

  return (
    <>
      <Grid item marginRight={2} paddingTop={2}>
        <DashboardRadioGroup
          key={currentValidity ?? "none"}
          defaultValue={currentValidity ?? undefined}
          row
          values={values}
          onChange={handleChange}
          label={
            <DashboardTypography variant="body2" sx={(theme) => ({ color: theme.palette.text.neutral.moderate })}>
              {allowAutomaticRenewal
                ? t("dashboard", "ticketing.renewalDate")
                : t("dashboard", "ticketing.expirationDate")}
            </DashboardTypography>
          }
          tooltipTextValues={{
            [MembershipValidityPeriod.AtDate]: t("dashboard", "ticketing.validityPeriod.atDateHelp") as string,
          }}
        />
      </Grid>
      <ConfirmRateChangeDialog
        open={isConfirmOpen}
        variant="date"
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmAtDate}
      />
    </>
  );
};
